import React, { useMemo } from 'react';
import { Box, Center, Table, Tbody, Td, Text, Th, Thead, Tr, VisuallyHidden } from '@chakra-ui/react';
import { Bar } from 'react-chartjs-2';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

// One series, so one colour (the dashboard blue, as in the timeline). Text uses ink
// tokens, never the series colour.
const BAR_COLOR = '#3182CE';
const AXIS_TEXT_COLOR = '#4A5568'; // gray.600
const GRID_COLOR = '#E2E8F0'; // gray.200
const MAX_TICK_LABEL_LENGTH = 18;

const truncate = (label) =>
  label.length > MAX_TICK_LABEL_LENGTH ? `${label.slice(0, MAX_TICK_LABEL_LENGTH - 1)}…` : label;

// Turns stats.foundational_skill_candidates into chart rows. The backend already returns
// every foundational skill (including zeros) sorted by count, so the order is kept.
export const buildFoundationCandidatesRows = (entries) =>
  (Array.isArray(entries) ? entries : [])
    .filter((entry) => entry && (entry.label || entry.skillid != null))
    .map((entry) => ({
      label: entry.label || `Skill ${entry.skillid}`,
      count: typeof entry.person_count === 'number' ? entry.person_count : 0
    }));

const candidatesLabel = (count) => `${count} candidate${count === 1 ? '' : 's'}`;

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  // Hovering anywhere in a skill's column shows its tooltip (hit area wider than the bar).
  interaction: { mode: 'index', intersect: false },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        autoSkip: false,
        maxRotation: 45,
        color: AXIS_TEXT_COLOR,
        callback(value) {
          return truncate(this.getLabelForValue(value));
        }
      }
    },
    y: {
      beginAtZero: true,
      ticks: { precision: 0, color: AXIS_TEXT_COLOR },
      grid: { color: GRID_COLOR },
      border: { display: false },
      title: { display: true, text: 'Candidates', color: AXIS_TEXT_COLOR }
    }
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (item) => candidatesLabel(item.parsed.y)
      }
    }
  }
};

// Vertical bar chart: new candidates per foundational skill in the last 30 days.
const FoundationSkillsCandidatesChart = ({ entries, emptyTextColor = 'gray.600' }) => {
  const rows = useMemo(() => buildFoundationCandidatesRows(entries), [entries]);

  const data = useMemo(
    () => ({
      labels: rows.map((row) => row.label),
      datasets: [
        {
          label: 'Candidates',
          data: rows.map((row) => row.count),
          backgroundColor: BAR_COLOR,
          hoverBackgroundColor: BAR_COLOR,
          // 4px rounded data-end; the baseline end stays square.
          borderRadius: { topLeft: 4, topRight: 4 },
          borderSkipped: 'start',
          maxBarThickness: 40,
          categoryPercentage: 0.8,
          barPercentage: 0.9
        }
      ]
    }),
    [rows]
  );

  if (rows.length === 0) {
    return (
      <Center minH="240px">
        <Text color={emptyTextColor}>No foundational skills defined yet.</Text>
      </Center>
    );
  }

  return (
    <>
      <Box minH="260px" role="img" aria-label="Bar chart of new candidates per foundational skill in the last 30 days">
        <Bar data={data} options={chartOptions} />
      </Box>
      <VisuallyHidden>
        <Table>
          <Thead>
            <Tr>
              <Th>Foundational skill</Th>
              <Th>Candidates in the last 30 days</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr key={row.label}>
                <Td>{row.label}</Td>
                <Td>{row.count}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </VisuallyHidden>
    </>
  );
};

export default FoundationSkillsCandidatesChart;
